import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
  ParametroGlobal,
  EstadoParametro,
  ParametroGlobalSede,
  HistorialCambioParametro,
  ParametroGlobalFiltro
} from '../models/parametro-global.model';
import { SessionService } from '@core';

const API_URL = environment.URL_SERVICIOSBACK;
const ENDPOINT = '/core/parametros-globales';

@Injectable({
  providedIn: 'root',
})
export class ParametrosGlobalesService {
  private readonly apiUrl = API_URL + ENDPOINT;
  private http = inject(HttpClient);
  private session = inject(SessionService);

  private usuarioActual(): string {
    return this.session.getCurrentUser()?.username ?? 'SISTEMA';
  }

  // ========== LISTADOS ==========

  listarTodos(): Observable<ParametroGlobal[]> {
    return this.http.get<any>(this.apiUrl).pipe(
      map(response => response?.aaData || response?.data || response || [])
    );
  }

  listarPorEmpresa(empresaId: number): Observable<ParametroGlobal[]> {
    const url = `${this.apiUrl}/empresa/${empresaId}`;
    return this.http.get<any>(url).pipe(
      map(response => response?.aaData || response?.data || response || [])
    );
  }

  listarPorEstado(estado: EstadoParametro): Observable<ParametroGlobal[]> {
    const url = `${this.apiUrl}/estado/${estado}`;
    return this.http.get<any>(url).pipe(
      map(response => response?.aaData || response?.data || response || [])
    );
  }

  listarConFiltros(filtros: ParametroGlobalFiltro): Observable<ParametroGlobal[]> {
    const empresaId = filtros.empresaId;
    const url = `${this.apiUrl}/empresa/${empresaId}/filtrar`;
    let params = new HttpParams();
    if (filtros.codigo) params = params.set('codigo', filtros.codigo);
    if (filtros.version != null) params = params.set('version', filtros.version.toString());
    if (filtros.estado) params = params.set('estado', filtros.estado);
    if (filtros.vigenciaDesde) params = params.set('fechaInicio', String(filtros.vigenciaDesde).substring(0, 10));
    if (filtros.vigenciaHasta) params = params.set('fechaFin', String(filtros.vigenciaHasta).substring(0, 10));
    return this.http.get<any>(url, { params }).pipe(
      map(response => response?.aaData || response?.data || response || [])
    );
  }

  // ========== CONSULTAS ESPECÍFICAS ==========

  obtenerDetalle(id: number): Observable<ParametroGlobal> {
    const url = `${this.apiUrl}/${id}/detalle`;
    return this.http.get<any>(url).pipe(
      map(response => response?.defaultObj || response?.data || response)
    );
  }

  obtenerActivoVigente(empresaId: number, fecha: Date | string): Observable<ParametroGlobal | null> {
    const fechaStr = fecha instanceof Date
      ? fecha.toISOString().substring(0, 10)
      : String(fecha).substring(0, 10);
    const url = `${this.apiUrl}/empresa/${empresaId}/activo-vigente`;
    const params = new HttpParams().set('fecha', fechaStr);
    return this.http.get<any>(url, { params }).pipe(
      map(response => response?.defaultObj || response?.data || response || null)
    );
  }

  // ========== CREAR Y MODIFICAR ==========

  private toRequestDTO(parametro: ParametroGlobal) {
    const p = parametro.planilla;
    return {
      empresaId:         parametro.empresaId,
      codigo:            parametro.codigo,
      vigenciaDesde:     parametro.vigenciaDesde,
      vigenciaHasta:     parametro.vigenciaHasta ?? '2099-12-31',
      aplicarTodasSedes: parametro.aplicarTodasSedes,
      asistencia:        parametro.asistencia,
      vacaciones:        parametro.vacaciones,
      general:           parametro.general,
      planilla: {
        ...p,
        conceptoDescuentoFaltaId: p?.conceptoDescuentoFaltaId || null,
        conceptoBaseGratiId:      p?.conceptoBaseGratiId      || null,
        conceptoBaseCtsId:        p?.conceptoBaseCtsId        || null,
      },
    };
  }

  crearParametroGlobal(parametro: ParametroGlobal): Observable<ParametroGlobal> {
    const params = new HttpParams().set('usuario', this.usuarioActual());
    return this.http.post<any>(this.apiUrl, this.toRequestDTO(parametro), { params }).pipe(
      map(response => response?.defaultObj || response?.data || response)
    );
  }

  actualizarParametroGlobal(id: number, parametro: ParametroGlobal): Observable<ParametroGlobal> {
    const url = `${this.apiUrl}/${id}`;
    const params = new HttpParams().set('usuario', this.usuarioActual());
    return this.http.put<any>(url, this.toRequestDTO(parametro), { params }).pipe(
      map(response => response?.defaultObj || response?.data || response)
    );
  }

  // ========== ACTIVAR/DESACTIVAR ==========

  activarParametroGlobal(id: number): Observable<string> {
    const url = `${this.apiUrl}/${id}/activar`;
    const params = new HttpParams().set('usuario', this.usuarioActual());
    return this.http.post(url, {}, { params, responseType: 'text' }).pipe(
      map(response => response || 'Parámetro activado exitosamente')
    );
  }

  desactivarParametroGlobal(id: number): Observable<string> {
    const url = `${this.apiUrl}/${id}/desactivar`;
    const params = new HttpParams().set('usuario', this.usuarioActual());
    return this.http.post(url, {}, { params, responseType: 'text' }).pipe(
      map(response => response || 'Parámetro desactivado exitosamente')
    );
  }

  // ========== CLONAR Y ELIMINAR ==========

  clonarParametroGlobal(id: number): Observable<ParametroGlobal> {
    const url = `${this.apiUrl}/${id}/clonar`;
    const params = new HttpParams().set('usuario', this.usuarioActual());
    return this.http.post<any>(url, {}, { params }).pipe(
      map(response => response?.defaultObj || response?.data || response)
    );
  }

  eliminarParametroGlobal(id: number): Observable<string> {
    const url = `${this.apiUrl}/${id}`;
    return this.http.delete(url, { responseType: 'text' }).pipe(
      map(response => response || 'Parámetro eliminado exitosamente')
    );
  }

  // ========== VALIDACIONES ==========

  verificarSuperposicion(empresaId: number, desde: Date | string, hasta: Date | string, parametroIdExcluido: number = 0): Observable<boolean> {
    const url = `${this.apiUrl}/validar/superposicion`;
    
    const desdeStr = desde instanceof Date ? desde.toISOString().substring(0, 10) : String(desde).substring(0, 10);
    const hastaStr = hasta instanceof Date ? hasta.toISOString().substring(0, 10) : String(hasta).substring(0, 10);
    
    let params = new HttpParams()
      .set('empresaId', empresaId.toString())
      .set('desde', desdeStr)
      .set('hasta', hastaStr)
      .set('parametroIdExcluido', parametroIdExcluido.toString());
    
    return this.http.get<any>(url, { params }).pipe(
      map(response => response?.defaultObj ?? response ?? false)
    );
  }

  existeParametroActivo(empresaId: number): Observable<boolean> {
    const url = `${this.apiUrl}/empresa/${empresaId}/existe-activo`;
    return this.http.get<any>(url).pipe(
      map(response => response?.defaultObj ?? response ?? false)
    );
  }

  // ========== GESTIÓN DE SEDES ==========

  listarSedes(parametroId: number): Observable<ParametroGlobalSede[]> {
    const url = `${this.apiUrl}/${parametroId}/sedes`;
    return this.http.get<any>(url).pipe(
      map(response => response?.aaData || response?.data || response || [])
    );
  }

  asignarSedes(parametroId: number, establecimientoIds: number[]): Observable<string> {
    const url = `${this.apiUrl}/${parametroId}/sedes`;
    const params = new HttpParams().set('usuario', this.usuarioActual());
    return this.http.post<any>(url, establecimientoIds, { params }).pipe(
      map(response => response?.msg || response || 'Sedes asignadas exitosamente')
    );
  }

  // ========== HISTORIAL ==========

  obtenerHistorialPorCodigo(codigo: string): Observable<ParametroGlobal[]> {
    const url = `${this.apiUrl}/codigo/${codigo}/historial`;
    return this.http.get<any>(url).pipe(
      map(response => response?.aaData || response?.data || response || [])
    );
  }

  obtenerHistorialAuditoria(parametroId: number): Observable<HistorialCambioParametro[]> {
    const url = `${this.apiUrl}/${parametroId}/historial-auditoria`;
    return this.http.get<any>(url).pipe(
      map(response => response?.aaData || response?.data || response || [])
    );
  }

  obtenerHistorialConFiltros(
    parametroId: number,
    seccion?: string,
    usuario?: string,
    accion?: string
  ): Observable<HistorialCambioParametro[]> {
    const url = `${this.apiUrl}/${parametroId}/historial-auditoria/filtros`;

    let params = new HttpParams();
    if (seccion) params = params.set('seccion', seccion);
    if (usuario) params = params.set('usuario', usuario);
    if (accion) params = params.set('accion', accion);

    return this.http.get<any>(url, { params }).pipe(
      map(response => response?.aaData || response?.data || response || [])
    );
  }
}